<template>
  <div class="py-12 space-y-6">
    <!-- === HEADER === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-2xl font-bold text-gray-800">
            {{ entityName }}
          </h2>
          <p class="text-sm text-gray-500 mt-1">
            {{ typeLabel }} • ID: {{ kycDocumentData?.userId || '-' }}
          </p>
        </div>
        <div
          :style="{
            backgroundColor: statusColor.background,
            color: statusColor.color,
          }"
          class="inline-block px-4 py-2 rounded-full font-semibold text-sm"
        >
          {{ statusLabel }}
        </div>
      </div>
    </div>

    <!-- {{ kycDocumentData }} -->
    <!-- === INFOS PERSONNELLES (Dynamic) === -->
    <div
      v-if="
        kycDocumentData?.kycData?.personalInfo &&
        Object.keys(kycDocumentData.kycData.personalInfo).length > 0
      "
      class="rounded-2xl border border-gray-200 bg-white p-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        Informations personnelles
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="(val, key) in filterEmpty(
            kycDocumentData.kycData.personalInfo,
          )"
          :key="key"
          :class="{hidden: key.includes('files')}"
        >
          <p class="text-sm text-gray-500">{{ formatKey(key) }}</p>
          <p class="font-medium text-gray-800">
            <template v-if="isUrl(val)">
              <a
                :href="val"
                target="_blank"
                download
                class="text-blue-600 hover:underline"
                >{{ val }}</a
              >
            </template>
            <template v-else>
              {{ formatValue(val) }}
            </template>
          </p>
        </div>
      </div>
    </div>

    <!-- === DOCUMENTS D'IDENTITÉ (Dynamic) === -->
    <div
      v-if="
        kycDocumentData?.kycData?.identityDocument &&
        Object.keys(kycDocumentData.kycData.identityDocument).length > 0
      "
      class="rounded-2xl border border-gray-200 bg-white p-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        Document d'identité
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="(val, key) in filterEmpty(
            kycDocumentData.kycData.identityDocument,
          )"
          :key="key"
        >
          <p class="text-sm text-gray-500">{{ formatKey(key) }}</p>
          <p class="font-medium text-gray-800">
            <template v-if="isImageUrl(val)">
              <div class="relative group mt-2">
                <img
                  :src="val"
                  @click="openImage(val)"
                  class="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer"
                />
                <a
                  :href="val"
                  target="_blank"
                  download
                  class="absolute bottom-2 right-2 bg-white/80 p-2 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <i class="pi pi-download text-gray-700"></i>
                </a>
              </div>
            </template>
            <template v-else-if="isUrl(val)">
              <a
                :href="val"
                target="_blank"
                class="text-blue-600 hover:underline"
                >Lien du document</a
              >
            </template>
            <template v-else>
              {{ formatValue(val) }}
            </template>
          </p>
        </div>
      </div>
    </div>

    <!-- === VÉHICULE (Dynamic) === -->
    <div
      v-if="
        kycDocumentData?.kycData?.vehicleDocument &&
        Object.keys(kycDocumentData.kycData.vehicleDocument).length > 0
      "
      class="rounded-2xl border border-gray-200 bg-white p-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        Document du véhicule
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="(val, key) in filterEmpty(
            kycDocumentData.kycData.vehicleDocument,
          )"
          :key="key"
        >
          <p class="text-sm text-gray-500">{{ formatKey(key) }}</p>
          <p class="font-medium text-gray-800">
            <template v-if="isImageUrl(val)">
              <div class="relative group mt-2">
                <img
                  :src="val"
                  @click="openImage(val)"
                  class="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer"
                />
                <a
                  :href="val"
                  target="_blank"
                  download
                  class="absolute bottom-2 right-2 bg-white/80 p-2 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <i class="pi pi-download text-gray-700"></i>
                </a>
              </div>
            </template>
            <template v-else-if="isUrl(val)">
              <a
                :href="val"
                target="_blank"
                class="text-blue-600 hover:underline"
                >Lien du document</a
              >
            </template>
            <template v-else>
              {{ formatValue(val) }}
            </template>
          </p>
        </div>
      </div>
    </div>

    <!-- === AUTRES PHOTOS (Selfie, etc) === -->
    <div
      v-if="kycDocumentData?.kycData?.selfieUrl"
      class="rounded-2xl border border-gray-200 bg-white p-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 mb-4">Selfie</h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div class="space-y-2 text-center">
          <img
            :src="kycDocumentData.kycData.selfieUrl"
            alt="Selfie"
            class="w-full h-40 object-cover rounded-lg border border-gray-200 cursor-pointer"
            @click="openImage(kycDocumentData.kycData.selfieUrl)"
          />
        </div>
      </div>
    </div>

    <!-- {{ kycDocumentData.kycData.personalInfo }} -->

    <!-- === PHOTOS (Restaurant) === -->
    <div
      v-if="isRestaurant && kycDocumentData?.pictures?.length"
      class="rounded-2xl border border-gray-200 bg-white p-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        Photos du restaurant
      </h3>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div
          v-for="(pic, index) in kycDocumentData.pictures"
          :key="index"
          class="relative"
        >
          <img
            :src="pic"
            alt="Photo restaurant"
            class="w-full h-40 object-cover rounded-lg border border-gray-200 cursor-pointer"
            @click="openImage(pic)"
          />
        </div>
      </div>
    </div>

    <!-- === DOCUMENTS JOINTS (Files) === -->
    <div
      v-if="
        kycDocumentData?.kycData?.personalInfo?.files &&
        kycDocumentData?.kycData?.personalInfo?.files.length > 0
      "
      class="rounded-2xl border border-gray-200 bg-white p-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 mb-4">Documents joints</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <a
          v-for="(file, index) in kycDocumentData?.kycData?.personalInfo?.files"
          :key="index"
          :href="file.url"
          target="_blank"
          class="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <div class="flex items-center space-x-3 overflow-hidden">
            <i class="pi pi-file text-blue-500 text-xl"></i>
            <div class="truncate">
              <p class="text-sm font-medium text-gray-700 truncate">
                {{ file.name }}
              </p>
              <p class="text-xs text-gray-400">{{ file.size }}</p>
            </div>
          </div>
          <div
            class="text-blue-600 hover:text-blue-800 p-2"
            title="Télécharger"
          >
            <ImgComponent
              v-if="Commons.isImgString(file.url)"
              :src="file.url"
              alt="Document"
              width="100"
              height="100"
              preview
              class="w-16 h-16 object-cover rounded"
            />
            <ImgComponent
              src=""
              alt="Document"
              class="w-16 h-16 object-cover rounded"
              v-else
            />
          </div>
        </a>
      </div>
    </div>

    <!-- === INFO SUPPLÉMENTAIRES === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        Informations supplémentaires
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-if="isRider && kycDocumentData?.emergencyContact">
          <p class="text-sm text-gray-500">Contact d'urgence</p>
          <p class="font-medium text-gray-800">
            {{ kycDocumentData.emergencyContact }}
          </p>
        </div>
        <div v-if="isRider && kycDocumentData?.bankAccount">
          <p class="text-sm text-gray-500">Compte bancaire</p>
          <p class="font-medium text-gray-800">
            {{ kycDocumentData.bankAccount }}
          </p>
        </div>
        <div v-if="kycDocumentData?.uploadedAt || kycDocumentData?.createdAt">
          <p class="text-sm text-gray-500">Soumis le</p>
          <p class="font-medium text-gray-800">
            {{
              Commons.time(
                kycDocumentData.uploadedAt || kycDocumentData.createdAt,
                'DD MMM YYYY HH:mm',
              )
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- === RAISON DE REJET === -->
    <div
      v-if="isRejected"
      class="rounded-2xl border border-red-200 bg-red-50 p-6"
    >
      <h3 class="text-lg font-semibold text-red-800 mb-2">Raison du rejet</h3>
      <p class="text-red-700">
        {{ kycDocumentData?.rejectionReason || 'Non spécifiée' }}
      </p>
    </div>

    <!-- === ACTIONS === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">Actions</h3>

      <div v-if="canValidate" class="space-y-3">
        <button
          @click="validateKyc"
          :disabled="isProcessing"
          v-if="kycDocumentData?.kycStatus !== 'approved'"
          class="w-full px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
        >
          <i class="pi pi-check mr-2"></i>
          Valider le KYC
        </button>

        <button
          @click="showRejectionModal = true"
          :disabled="isProcessing"
          v-if="
            kycDocumentData?.kycStatus !== 'rejected' &&
            kycDocumentData?.kycStatus !== 'approved'
          "
          class="w-full px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
        >
          <i class="pi pi-times mr-2"></i>
          Rejeter le KYC
        </button>
      </div>

      <div v-if="canDelete" class="mt-4 pt-4 border-t">
        <button
          @click="confirmDelete"
          :disabled="isProcessing"
          class="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-700 rounded-lg font-medium transition-colors"
        >
          <i class="pi pi-trash mr-2"></i>
          Supprimer le document
        </button>
      </div>
    </div>

    <!-- === Modal de rejet === -->
    <div
      v-if="showRejectionModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelRejection"
    >
      <div class="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">
          Raison du rejet
        </h3>

        <textarea
          v-model="rejectionReason"
          class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800"
          placeholder="Expliquez pourquoi ce KYC est rejeté..."
          rows="4"
        ></textarea>

        <div class="flex gap-3 mt-4">
          <button
            @click="rejectKyc"
            :disabled="!rejectionReason.trim() || isProcessing"
            class="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
          >
            Confirmer le rejet
          </button>
          <button
            @click="cancelRejection"
            class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>

    <!-- === Action Triggers === -->
    <ActionTrigger
      v-if="isRider"
      dataKey="processRiderKyc"
      ref="processKycTrigger"
      @success="onKycProcessed"
      @failed="onKycError"
    />
    <ActionTrigger
      v-if="isRestaurant"
      dataKey="processRestaurantKyc"
      ref="processKycTrigger"
      @success="onKycProcessed"
      @failed="onKycError"
    />
    <ActionTrigger
      dataKey="deleteKyc"
      ref="deleteKycTrigger"
      @success="onKycDeleted"
      @failed="onKycError"
    />
    <ActionTrigger
      dataKey="updateRestaurant"
      ref="updateRestaurantTriggerRef"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import Commons from '@/shared/utils/commons'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import {
  getKycStatusDisplay,
  getKycStatusColor,
} from '@/shared/formatters/kycFormatter.js'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'

// Key mappings for better readability
const KEY_MAP = {
  firstName: 'Prénom',
  lastName: 'Nom',
  dateOfBirth: 'Date de naissance',
  birthDate: 'Date de naissance',
  gender: 'Sexe',
  nationality: 'Nationalité',
  phone: 'Téléphone',
  phoneNumber: 'Téléphone',
  email: 'Email',
  companyName: "Nom de l'entreprise",
  address: 'Adresse',
  city: 'Ville',
  state: 'Région / État',
  country: 'Pays',
  countryCode: 'Code pays',
  postalCode: 'Code postal',
  emergencyContact: "Contact d'urgence",
  bankAccount: 'Compte bancaire',
  website: 'Site web',
  whatsappPhoneNumber: 'WhatsApp',
  type: 'Type',
  number: 'Numéro',
  expirationDate: "Date d'expiration",
  licenseNumber: 'N° de permis',
  licenseExpiry: 'Expiration du permis',
  commercialLicenceId: 'Registre de commerce',
}

function formatKey(key) {
  if (KEY_MAP[key]) return KEY_MAP[key]
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
}

function filterEmpty(obj) {
  if (!obj) return {}
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, v]) => v !== null && v !== undefined && v !== '' && !isImageUrl(v),
    ),
  )
}

function isUrl(str) {
  if (typeof str !== 'string') return false
  return str.startsWith('http://') || str.startsWith('https://')
}

function isImageUrl(str) {
  if (!isUrl(str)) return false
  return (
    /\.(jpeg|jpg|gif|png)$/i.test(str) ||
    str.includes('firebasestorage') ||
    str.includes('cloudinary')
  )
}

function formatValue(val) {
  if (typeof val === 'boolean') return val ? 'Oui' : 'Non'
  if (Array.isArray(val)) return val.join(', ')
  return val
}

const props = defineProps({
  kycDocumentData: {
    type: Object,
    default: () => ({}),
  },
  restaurants: {
    type: Array,
    default: () => [],
  },
  riders: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['refresh'])

const processKycTrigger = ref(null)
const deleteKycTrigger = ref(null)

const isProcessing = ref(false)
const showRejectionModal = ref(false)
const rejectionReason = ref('')

const isRider = computed(() => props.kycDocumentData?.type === 'rider')
const isRestaurant = computed(
  () => props.kycDocumentData?.type === 'restaurant',
)

const statusLabel = computed(() =>
  getKycStatusDisplay(props.kycDocumentData?.kycStatus),
)
const statusColor = computed(() =>
  getKycStatusColor(props.kycDocumentData?.kycStatus),
)
const isRejected = computed(
  () =>
    props.kycDocumentData?.kycStatus === 'rejected' ||
    props.kycDocumentData?.kycStatus === 'Rejeté',
)

const canValidate = computed(() => {
  const status = props.kycDocumentData?.kycStatus
  return status !== 'draft'
})

const canDelete = computed(() => true) // Admin peut toujours supprimer

const typeLabel = computed(() => {
  return isRider.value ? 'Livreur' : isRestaurant.value ? 'Restaurant' : '-'
})

const entityName = computed(() => {
  if (isRider.value) {
    return (
      props.kycDocumentData?.fullName || props.kycDocumentData?.userName || '-'
    )
  }
  if (isRestaurant.value) {
    return (
      props.kycDocumentData?.storeName || props.kycDocumentData?.userName || '-'
    )
  }
  return props.kycDocumentData?.userName || '-'
})

async function validateKyc() {
  if (isProcessing.value || !processKycTrigger.value) return

  isProcessing.value = true

  try {
    Events.dispatch(EVENTS.APPLICATION_IS_WORKING, {
      canDismiss: false,
      title: 'Validation du KYC',
      subTitle: 'Veuillez patienter...',
    })

    const payload = {
      id: props.kycDocumentData.id,
      kycId: props.kycDocumentData.id,
      userId: props.kycDocumentData.userId,
      action: 'validate',
      type: props.kycDocumentData.type,
    }

    if (isRider.value) {
      payload.riderId = props.kycDocumentData.userId
      payload.kycData = props.kycDocumentData.kycData || {}
    } else if (isRestaurant.value) {
      payload.storeId = props.kycDocumentData.userId
      payload.kycData = props.kycDocumentData.kycData || {}
    }

    await processKycTrigger.value.trigger(payload)
  } catch (error) {
    Events.dispatch(EVENTS.APPLICATION_WORK_FINISHED)
    onKycError(error)
  } finally {
    isProcessing.value = false
  }
}

async function rejectKyc() {
  if (
    isProcessing.value ||
    !processKycTrigger.value ||
    !rejectionReason.value.trim()
  )
    return

  isProcessing.value = true

  try {
    Events.dispatch(EVENTS.APPLICATION_IS_WORKING, {
      canDismiss: false,
      title: 'Rejet du KYC',
      subTitle: 'Veuillez patienter...',
    })

    const payload = {
      id: props.kycDocumentData.id,
      kycId: props.kycDocumentData.id,
      userId: props.kycDocumentData.userId,
      action: 'reject',
      rejectionReason: rejectionReason.value,
      type: props.kycDocumentData.type,
    }

    if (isRider.value) {
      payload.riderId = props.kycDocumentData.userId
      payload.kycData = props.kycDocumentData.kycData || {}
    } else if (isRestaurant.value) {
      payload.storeId = props.kycDocumentData.userId
      payload.kycData = props.kycDocumentData.kycData || {}
    }

    await processKycTrigger.value.trigger(payload)
  } catch (error) {
    Events.dispatch(EVENTS.APPLICATION_WORK_FINISHED)
    onKycError(error)
  } finally {
    isProcessing.value = false
  }
}

const updateRestaurantTriggerRef = ref(null)

function cancelRejection() {
  showRejectionModal.value = false
  rejectionReason.value = ''
}

async function confirmDelete() {
  const res = await window.customPrompt({
    title: `Supprimer le KYC de ${entityName.value} ?`,
    description: 'Cette action est irréversible.',
    formFields: [],
    successText: 'Supprimer',
    cancelText: 'Annuler',
  })

  if (!res.accepted) return

  isProcessing.value = true

  try {
    Events.dispatch(EVENTS.APPLICATION_IS_WORKING, {
      canDismiss: false,
      title: 'Suppression du KYC',
      subTitle: 'Veuillez patienter...',
    })

    await deleteKycTrigger.value.trigger({
      id: props.kycDocumentData.id,
      type: props.kycDocumentData.type,
    })
  } catch (error) {
    Events.dispatch(EVENTS.APPLICATION_WORK_FINISHED)
    onKycError(error)
  } finally {
    isProcessing.value = false
  }
}

async function onKycProcessed() {
  // Automatically set restaurant status to 'submitted' on KYC validation
  if (isRestaurant.value && updateRestaurantTriggerRef.value) {
    await updateRestaurantTriggerRef.value.trigger({
      id: props.kycDocumentData.userId,
      status: 'approved',
    })
  }

  Events.dispatch(EVENTS.APPLICATION_WORK_FINISHED)

  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Le KYC a été traité avec succès',
  })

  emit('refresh')
}

function onKycDeleted() {
  Events.dispatch(EVENTS.APPLICATION_WORK_FINISHED)

  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Le KYC a été supprimé',
  })

  emit('refresh')
}

function onKycError(error) {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: error.result?.message || 'Une erreur est survenue',
  })
}

function openImage(url) {
  window.open(url, '_blank')
}
</script>

<style scoped>
.title-color {
  color: #1f2937;
}
</style>
